import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pill } from '../components/ui/Pill'
import { Icon, I } from '../components/ui/icons'
import { TEXT_WRAP_STYLE } from '../components/ui'
import { chooseChannelOnboardingStockOwner } from './channelsApi'
import type { ChannelOnboardingJob, ChannelOnboardingStockOwner } from './types'

interface OnboardingStockOwnerChoiceProps {
  accessToken: string
  job: ChannelOnboardingJob | null
  onJobChange: (job: ChannelOnboardingJob) => void
}

function isCompleted(job: ChannelOnboardingJob): boolean {
  return job.status === 'COMPLETED' || job.status === 'COMPLETED_WITH_ISSUES'
}

// Asks who owns stock after the first Salla/Zid import. The backend only offers
// the choice (non-null stockOwnerAfterImport) when the channel became the stock
// owner on connect; the import runs first, then stock is handed to Tlaaq.
export function OnboardingStockOwnerChoice({ accessToken, job, onJobChange }: OnboardingStockOwnerChoiceProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)

  if (!job || job.stockOwnerAfterImport === null) return null
  const store = t(`channels.providers.${job.provider}`)

  if (job.stockOwnerAppliedAt) {
    return (
      <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)', padding: '13px 14px', borderRadius: 'var(--r-md)', border: '1px solid var(--success)', background: 'var(--success-100)', textAlign: 'start', ...TEXT_WRAP_STYLE }}>
        <span style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} stroke={2.5} color="var(--success-700)">{I.check}</Icon>
        </span>
        <span style={{ display: 'grid', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: 'var(--fw-extra)', lineHeight: '18px', color: 'var(--success-700)' }}>{t('channels.onboardingStockOwner.applied.title')}</span>
          <span style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--fg-muted)' }}>{t('channels.onboardingStockOwner.applied.body', { store })}</span>
        </span>
      </div>
    )
  }

  if (isCompleted(job)) return null

  const choose = async (stockOwner: ChannelOnboardingStockOwner) => {
    if (saving || stockOwner === job.stockOwnerAfterImport) return
    setSaving(true)
    setError(false)
    try {
      onJobChange(await chooseChannelOnboardingStockOwner(accessToken, job.id, stockOwner))
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  const options: { value: ChannelOnboardingStockOwner; title: string; body: string; recommended: boolean }[] = [
    { value: 'TLAAQ', title: t('channels.onboardingStockOwner.tlaaq.title'), body: t('channels.onboardingStockOwner.tlaaq.body', { store }), recommended: true },
    { value: 'CHANNEL', title: store, body: t('channels.onboardingStockOwner.channel.body', { store }), recommended: false },
  ]

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-3)', textAlign: 'start', minWidth: 0 }}>
      <div style={{ display: 'grid', gap: 'var(--sp-1)' }}>
        <div id="onboarding-stock-owner-title" style={{ fontSize: 'var(--fs-16)', fontWeight: 'var(--fw-extra)', lineHeight: '22px', color: 'var(--brand-900)' }}>
          {t('channels.onboardingStockOwner.title')}
        </div>
        <div style={{ fontSize: 'var(--fs-14)', lineHeight: '20px', color: 'var(--fg-muted)' }}>
          {t('channels.onboardingStockOwner.subtitle', { store })}
        </div>
      </div>
      <div role="radiogroup" aria-labelledby="onboarding-stock-owner-title" style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        {options.map(option => {
          const selected = job.stockOwnerAfterImport === option.value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={saving}
              onClick={() => void choose(option.value)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)', padding: '13px 14px', textAlign: 'start',
                borderRadius: 'var(--r-md)', border: selected ? '1.5px solid var(--brand-500)' : '1px solid var(--border-subtle)',
                background: selected ? 'var(--brand-100)' : 'white', cursor: saving ? 'progress' : 'pointer', fontFamily: 'inherit', minWidth: 0,
              }}
            >
              <span style={{ display: 'grid', gap: 3, flex: 1, minWidth: 0, ...TEXT_WRAP_STYLE }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 'var(--fw-extra)', lineHeight: '18px', color: 'var(--brand-900)' }}>{option.title}</span>
                  {option.recommended && <Pill tone="coral" size="sm">{t('channels.onboardingStockOwner.recommended')}</Pill>}
                </span>
                <span style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--fg-muted)' }}>{option.body}</span>
              </span>
              <span style={{ width: 20, height: 20, flexShrink: 0, boxSizing: 'border-box', borderRadius: '50%', border: selected ? '6px solid var(--brand-500)' : '2px solid var(--border-default)', background: 'white' }} />
            </button>
          )
        })}
      </div>
      {error
        ? <div role="alert" style={{ fontSize: 'var(--fs-12)', lineHeight: '16px', color: 'var(--danger)' }}>{t('channels.onboardingStockOwner.error')}</div>
        : (
          <div style={{ fontSize: 'var(--fs-12)', lineHeight: '16px', color: 'var(--fg-subtle)' }}>
            {job.stockOwnerAfterImport === 'TLAAQ'
              ? t('channels.onboardingStockOwner.hint.tlaaq')
              : t('channels.onboardingStockOwner.hint.channel', { store })}
          </div>
        )}
    </div>
  )
}
